import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import type { ReactElement } from "react";
import * as React from "react";

import { cn } from "@/lib/utils";

import { Slot } from "./_internal/slot";

const buttonVariants = cva(
  "focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/40 aria-invalid:border-destructive/50 rounded-lg border border-transparent bg-clip-padding text-sm font-medium focus-visible:ring-1 aria-invalid:ring-1 [&_svg:not([class*='size-'])]:size-4 inline-flex items-center justify-center whitespace-nowrap transition-all duration-200 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none group/button select-none",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-md hover:shadow-primary/20 [a]:hover:bg-primary/90",
        outline:
          "border-input bg-input/30 hover:bg-input/50 hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "hover:bg-muted/50 hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground",
        destructive:
          "bg-destructive/20 hover:bg-destructive/30 focus-visible:ring-destructive/40 text-destructive focus-visible:border-destructive/40",
        link: "text-primary underline-offset-4 hover:underline",
      },
      // Phones get a 44px floor (`max-sm:min-*`) rather than a phone height, so a caller's own
      // `h-12` or `size-12` still wins. `xs` sizes stay dense for inline use.
      size: {
        default:
          "h-9 max-sm:min-h-11 gap-1.5 px-3 has-data-[icon=inline-end]:pr-2.5 has-data-[icon=inline-start]:pl-2.5",
        xs: "h-7 gap-1 px-2.5 text-xs has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 max-sm:min-h-11 gap-1 px-3 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-10 max-sm:min-h-11 gap-1.5 px-4 has-data-[icon=inline-end]:pr-3.5 has-data-[icon=inline-start]:pl-3.5",
        icon: "size-9 max-sm:min-h-11 max-sm:min-w-11",
        "icon-xs": "size-7 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8 max-sm:min-h-11 max-sm:min-w-11",
        "icon-lg": "size-10 max-sm:min-h-11 max-sm:min-w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    /** When provided, clones this element and forwards Button's props/className to it (Base-UI `render` parity). */
    render?: ReactElement;
  };

function Button({
  className,
  variant = "default",
  size = "default",
  render,
  type,
  children,
  ...props
}: ButtonProps) {
  const classes = cn(buttonVariants({ variant, size, className }));

  if (render) {
    const renderWithChildren =
      children !== undefined ? React.cloneElement(render, undefined, children) : render;
    return (
      <Slot data-slot="button" className={classes} {...props}>
        {renderWithChildren}
      </Slot>
    );
  }

  return (
    <button data-slot="button" type={type ?? "button"} className={classes} {...props}>
      {children}
    </button>
  );
}

export { Button, buttonVariants };
export type { ButtonProps };
